import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import type { JourneyNode } from "@/lib/journey-api";
import type { JourneyPlanStep } from "@/lib/journey-bindings";
import { applySlotShortcut, shanghaiWallToIso } from "@/lib/plan-schedule-reflow";
import {
  DEFAULT_SEND_SLOTS,
  PAST_TIME_HINT,
  SEND_NOW_HINT,
  confirmButtonLabel,
  countScheduleReflows,
  defaultScheduleWall,
  isScheduleInFuture,
  isSlotPastOnDate,
  normalizeWallTime,
  reflowFollowupCopy,
  scheduleModeHint,
  type SendPlanStepMode,
} from "@/lib/send-plan-step-dialog";
import { cn } from "@/lib/utils";

export type SendPlanStepDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  confirming: boolean;
  onConfirmNow: () => void | Promise<void>;
  onConfirmSchedule: (plannedAtIso: string) => void | Promise<void>;
  now?: Date;
  slots?: string[];
  currentStepId: number | null;
  steps: JourneyPlanStep[];
  nodes: Pick<JourneyNode, "id" | "offset_days">[];
  inFlightStepIds: number[];
  sentStepIds: number[];
};

/** 立即发送确认框：立刻出站 或 定时改期（规格 §3） */
export function SendPlanStepDialog({
  open,
  onOpenChange,
  confirming,
  onConfirmNow,
  onConfirmSchedule,
  now,
  slots = DEFAULT_SEND_SLOTS,
  currentStepId,
  steps,
  nodes,
  inFlightStepIds,
  sentStepIds,
}: SendPlanStepDialogProps) {
  const [mode, setMode] = useState<SendPlanStepMode>("now");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");

  useEffect(() => {
    if (!open) {
      setMode("now");
      setSelectedDate("");
      setSelectedTime("");
      return;
    }
    setMode("now");
    const wall = defaultScheduleWall(now ?? new Date(), slots);
    setSelectedDate(wall.date);
    setSelectedTime(wall.time);
  }, [open, now, slots]);

  const clock = now ?? new Date();
  const insertSource = steps.find((s) => s.id === currentStepId)?.insert_source ?? null;
  const scheduleFuture = isScheduleInFuture(selectedDate, selectedTime, clock);
  const confirmDisabled = confirming || (mode === "schedule" && !scheduleFuture);

  const reflowN = useMemo(
    () =>
      countScheduleReflows({
        now: clock,
        currentStepId,
        selectedDate,
        selectedTime,
        steps,
        nodes,
        inFlightStepIds,
        sentStepIds,
      }),
    [
      clock,
      currentStepId,
      selectedDate,
      selectedTime,
      steps,
      nodes,
      inFlightStepIds,
      sentStepIds,
    ],
  );

  function handleOpenChange(next: boolean) {
    if (confirming) return;
    onOpenChange(next);
  }

  function handleConfirm() {
    if (confirmDisabled) return;
    if (mode === "now") {
      void onConfirmNow();
      return;
    }
    const hhmm = normalizeWallTime(selectedTime) ?? selectedTime;
    void onConfirmSchedule(shanghaiWallToIso(selectedDate, hhmm));
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md" showCloseButton={!confirming}>
        <DialogHeader>
          <DialogTitle>发送这一步？</DialogTitle>
          <DialogDescription className="sr-only">
            选择立刻创建企微待确认任务，或只改计划时间。
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <label
            className={cn(
              "flex cursor-pointer gap-3 rounded-2xl border p-3",
              mode === "now" ? "border-foreground/20 bg-muted/40" : "border-border",
            )}
          >
            <input
              type="radio"
              name="send-plan-step-mode"
              value="now"
              checked={mode === "now"}
              disabled={confirming}
              className="mt-1"
              onChange={() => setMode("now")}
            />
            <span>
              <span className="block font-medium">立刻发送</span>
              <span className="mt-1 block text-sm text-muted-foreground">{SEND_NOW_HINT}</span>
            </span>
          </label>

          <label
            className={cn(
              "flex cursor-pointer gap-3 rounded-2xl border p-3",
              mode === "schedule" ? "border-foreground/20 bg-muted/40" : "border-border",
            )}
          >
            <input
              type="radio"
              name="send-plan-step-mode"
              value="schedule"
              checked={mode === "schedule"}
              disabled={confirming}
              className="mt-1"
              onChange={() => setMode("schedule")}
            />
            <span>
              <span className="block font-medium">定时发送</span>
              <span className="mt-1 block text-sm text-muted-foreground">
                {scheduleModeHint(insertSource)}
              </span>
            </span>
          </label>

          {mode === "schedule" && (
            <div className="flex flex-col gap-3 pl-7">
              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1.5">
                  <span className="text-xs text-muted-foreground">日期</span>
                  <Input
                    type="date"
                    value={selectedDate}
                    disabled={confirming}
                    onChange={(e) => setSelectedDate(e.target.value)}
                  />
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="text-xs text-muted-foreground">时刻</span>
                  <Input
                    type="time"
                    step={60}
                    value={selectedTime}
                    disabled={confirming}
                    onChange={(e) =>
                      setSelectedTime(normalizeWallTime(e.target.value) ?? e.target.value)
                    }
                  />
                </label>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-muted-foreground">快捷：</span>
                {slots.map((slot) => (
                  <Button
                    key={slot}
                    type="button"
                    size="xs"
                    variant="outline"
                    disabled={confirming}
                    className={cn(isSlotPastOnDate(selectedDate, slot, clock) && "opacity-50")}
                    onClick={() => {
                      const next = applySlotShortcut({
                        now: clock,
                        selectedDate,
                        slot,
                      });
                      setSelectedDate(next.date);
                      setSelectedTime(next.time);
                    }}
                  >
                    {slot}
                  </Button>
                ))}
              </div>

              <p className="text-sm text-muted-foreground">{reflowFollowupCopy(reflowN)}</p>

              {!scheduleFuture && (
                <p className="text-sm text-destructive">{PAST_TIME_HINT}</p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={confirming}
            onClick={() => handleOpenChange(false)}
          >
            取消
          </Button>
          <Button type="button" disabled={confirmDisabled} onClick={handleConfirm}>
            {confirmButtonLabel(mode, confirming)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
